import { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { AdminFeedbackItem, FeedbackCategory, getAdminFeedback } from '../api';

const CATEGORY_LABELS: Record<FeedbackCategory, string> = {
  bug: 'Bug',
  feature: 'Feature Request',
  question: 'Question',
  other: 'Other',
};

const CATEGORIES: FeedbackCategory[] = ['bug', 'feature', 'question', 'other'];

function formatDate(dateStr: string): string {
  const date = new Date(dateStr);
  const now = new Date();
  const diffMs = now.getTime() - date.getTime();
  const diffHours = diffMs / (1000 * 60 * 60);

  if (diffHours < 1) {
    const diffMin = Math.floor(diffMs / (1000 * 60));
    return diffMin <= 1 ? 'Just now' : `${diffMin}m ago`;
  }
  if (diffHours < 24) {
    return `${Math.floor(diffHours)}h ago`;
  }
  if (diffHours < 48) {
    return 'Yesterday';
  }

  return date.toLocaleDateString(undefined, {
    month: 'short',
    day: 'numeric',
    year: date.getFullYear() !== now.getFullYear() ? 'numeric' : undefined,
  });
}

export default function AdminFeedback() {
  const [feedback, setFeedback] = useState<AdminFeedbackItem[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [categoryFilter, setCategoryFilter] = useState<FeedbackCategory | ''>('');

  useEffect(() => {
    async function fetchFeedback() {
      setLoading(true);
      try {
        const data = await getAdminFeedback(categoryFilter || undefined);
        setFeedback(data);
        setError(null);
      } catch (err) {
        setError(err instanceof Error ? err.message : 'Failed to load feedback');
      } finally {
        setLoading(false);
      }
    }

    fetchFeedback();
  }, [categoryFilter]);

  if (loading) {
    return (
      <div className="card">
        <p>Loading feedback...</p>
      </div>
    );
  }

  if (error) {
    return (
      <div className="card">
        <p className="error">{error}</p>
      </div>
    );
  }

  return (
    <div>
      <div style={{ display: 'flex', alignItems: 'center', gap: '1rem', marginBottom: '1rem' }}>
        <Link to="/admin" style={{ color: '#4b7bec', textDecoration: 'none', fontSize: '14px' }}>
          &larr; Admin
        </Link>
        <h2 style={{ margin: 0 }}>Feedback ({feedback.length})</h2>
      </div>

      <div className="card" style={{ marginBottom: '1.5rem' }}>
        <div className="feedback-filter-bar">
          <label htmlFor="category-filter" style={{ fontSize: '14px', opacity: 0.7 }}>
            Filter by category:
          </label>
          <select
            id="category-filter"
            value={categoryFilter}
            onChange={(e) => setCategoryFilter(e.target.value as FeedbackCategory | '')}
            className="feedback-filter-select"
          >
            <option value="">All</option>
            {CATEGORIES.map((cat) => (
              <option key={cat} value={cat}>
                {CATEGORY_LABELS[cat]}
              </option>
            ))}
          </select>
        </div>
      </div>

      {feedback.length === 0 ? (
        <div className="card">
          <p style={{ opacity: 0.6 }}>
            {categoryFilter
              ? `No ${CATEGORY_LABELS[categoryFilter].toLowerCase()} feedback yet.`
              : 'No feedback yet.'}
          </p>
        </div>
      ) : (
        <div className="feedback-list">
          {feedback.map((item) => (
            <div key={item.id} className="card feedback-card">
              <div className="feedback-card-header">
                <div className="feedback-user">
                  {item.user_picture ? (
                    <img src={item.user_picture} alt="" className="user-avatar-small" />
                  ) : (
                    <div className="user-avatar-small placeholder">
                      {item.user_name?.[0]?.toUpperCase() ||
                        item.user_email[0]?.toUpperCase() ||
                        '?'}
                    </div>
                  )}
                  <div>
                    <span className="feedback-user-name">{item.user_name || item.user_email}</span>
                    {item.user_name && (
                      <span className="feedback-user-email">{item.user_email}</span>
                    )}
                  </div>
                </div>
                <div className="feedback-meta">
                  <span className={`feedback-category-badge category-${item.category}`}>
                    {CATEGORY_LABELS[item.category]}
                  </span>
                  <span className="feedback-date">{formatDate(item.created_at)}</span>
                </div>
              </div>

              <p className="feedback-message">{item.message}</p>

              <div className="feedback-card-footer">
                {item.platform && (
                  <span className="feedback-detail">{item.platform === 'ios' ? 'iOS' : 'Web'}</span>
                )}
                {item.app_version && <span className="feedback-detail">v{item.app_version}</span>}
                {item.device_info && <span className="feedback-detail">{item.device_info}</span>}
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
